import { thermostatSettingsCollection } from '@/reducers/thermostatSettings'
import { thermostatActiveSettingsCollection } from '@/reducers/thermostatActiveSettings'
import { thermostatDemandResponseEventsCollection } from '@/reducers/thermostatDemandResponseEvents'
import { ThermostatSettings } from '@/types'
import { CostCategoryDisplay } from '@/types/thermostatCostCategoryControls'
import { eventIsCurrentlyActive } from '@/types/deviceDemandResponseEvent'
import { useAppDispatch } from '@/hooks'
import { useCallback, useEffect, useState } from 'react'
import { debounce } from 'lodash'

interface UseCostCategoriesToggleStatusParams {
  thermostatId: number
  thermostatSettings: ThermostatSettings | undefined
  forcedCostCategoryControlState: boolean | null | undefined
  isInInactivePeriod: boolean
  isToggleDisabled: boolean
  costCategories: CostCategoryDisplay[]
}

export default function useCostCategoriesToggleStatus({
  thermostatId,
  thermostatSettings,
  forcedCostCategoryControlState,
  isInInactivePeriod,
  isToggleDisabled,
  costCategories,
}: UseCostCategoriesToggleStatusParams) {
  const dispatch = useAppDispatch()
  const [saveMoneyEnabled, setSaveMoneyEnabled] = useState(false)

  // Fetch DR events for this thermostat
  const { data: drEvents } = thermostatDemandResponseEventsCollection.useFetch()

  // Find active DR event for this thermostat (enrolled and currently within time window)
  const activeDrEvent = drEvents?.find(
    (event) =>
      event.thermostat_id === thermostatId &&
      event.enrolled &&
      eventIsCurrentlyActive(event),
  )
  const isActiveDREvent = Boolean(activeDrEvent)

  // Modal state for when all cost categories are set to 0
  const [isZeroCategoriesModalOpen, setIsZeroCategoriesModalOpen] =
    useState(false)

  // Check if all cost categories have temperature shifts set to 0
  const allCostCategoriesSetToZero =
    costCategories.length > 0 &&
    costCategories.every((category) => category.initValue === 0)

  useEffect(() => {
    // Precedence order:
    // - If all cost categories are set to 0, force toggle off
    if (allCostCategoriesSetToZero) {
      setSaveMoneyEnabled(false)
      return
    }

    // - If there's an active DR event, force toggle off
    if (isActiveDREvent) {
      setSaveMoneyEnabled(false)
      return
    }

    // - If enrollment forces a state, reflect that regardless of inactivity
    if (forcedCostCategoryControlState != null) {
      setSaveMoneyEnabled(Boolean(forcedCostCategoryControlState))
      return
    }

    // If we are in an inactive period, force the toggle off
    if (isInInactivePeriod) {
      setSaveMoneyEnabled(false)
      return
    }

    // Otherwise, reflect the current optimization setting
    setSaveMoneyEnabled(
      thermostatSettings?.cost_category_optimization_enabled ?? false,
    )
  }, [
    thermostatSettings?.cost_category_optimization_enabled,
    forcedCostCategoryControlState,
    isInInactivePeriod,
    allCostCategoriesSetToZero,
    isActiveDREvent,
  ])

  // Check if program is forcing a state (valid boolean)
  const isProgramOverride =
    forcedCostCategoryControlState !== null &&
    forcedCostCategoryControlState !== undefined

  const onToggleChange = useCallback(
    debounce((value: boolean) => {
      if (isToggleDisabled) return

      // If trying to change when program is overriding, show modal instead
      if (isProgramOverride) {
        setIsZeroCategoriesModalOpen(true)
        return
      }

      // If trying to turn on when there's an active DR event, show modal instead
      if (value && isActiveDREvent) {
        setIsZeroCategoriesModalOpen(true)
        return
      }

      // If trying to turn on when all categories are 0, show modal instead
      if (value && allCostCategoriesSetToZero) {
        setIsZeroCategoriesModalOpen(true)
        return
      }

      setSaveMoneyEnabled(value)
      const updatedSettings = { cost_category_optimization_enabled: value }
      dispatch(
        thermostatSettingsCollection.actions.update(
          `/thermostats/${thermostatId}/settings/`,
          updatedSettings,
          { id: thermostatSettings?.id },
        ),
      )
      dispatch(thermostatActiveSettingsCollection.actions.invalidate())
    }, 200),
    [
      dispatch,
      thermostatId,
      thermostatSettings?.id,
      isToggleDisabled,
      allCostCategoriesSetToZero,
      isActiveDREvent,
      isProgramOverride,
    ],
  )

  const openZeroCategoriesModal = useCallback(() => {
    setIsZeroCategoriesModalOpen(true)
  }, [])

  const closeZeroCategoriesModal = useCallback(() => {
    setIsZeroCategoriesModalOpen(false)
  }, [])

  return {
    saveMoneyEnabled,
    onToggleChange,
    allCostCategoriesSetToZero,
    isZeroCategoriesModalOpen,
    openZeroCategoriesModal,
    closeZeroCategoriesModal,
    isActiveDREvent,
    activeDrEvent,
    isProgramOverride,
  }
}
